/**
 * POST /allocation-questionnaire/preference/submit.
 *
 * Belongs here: path, axios, mutation, toast + status refetch on save.
 * Does not belong here: navigation after save.
 */
import { useCallback, useEffect, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import axiosInstance from '../../../../utils/axiosConfig'
import { mobileBackendUrl } from '../../../../env'
import { useToast } from '../../../../state-management/contexts/ToastContext'
import type {
  PreferencesAnswersMap,
  SubmitPreferencesApiRequest,
  SubmitPreferencesApiResponse
} from '../preferences/setup/types'
import { buildSubmitPreferencesPayload } from './submitPreferencesPayload'
import { runExpertReviewSubmit } from './runExpertReviewSubmit'
import {
  createAnswersSubmitQueue,
  enqueueAnswersSubmit,
  type AnswersSubmitQueue
} from './enqueueAnswersSubmit'

export const PREFERENCES_ANSWERS_SUBMIT_PATH = `${mobileBackendUrl}/v2/portfolio/allocation-questionnaire/preference/submit`

export type SubmitPreferencesAnswersVars = {
  questionnaireId: string
  answers: PreferencesAnswersMap
  /** Snapshot of committedAnswersVersion at POST time — MarkSubmitted uses this, not live state. */
  answersVersion: number
}

/** POST preferences answers. */
export async function submitPreferencesAnswers(
  payload: SubmitPreferencesApiRequest
): Promise<SubmitPreferencesApiResponse> {
  const response = await axiosInstance.post<SubmitPreferencesApiResponse>(
    PREFERENCES_ANSWERS_SUBMIT_PATH,
    payload
  )
  if (response?.data != null) {
    return response.data
  }
  if (response?.status === 200) {
    return { success: true }
  }
  throw new Error('Failed to submit preferences answers')
}

type UseSubmitPreferencesMutationOptions = {
  /**
   * Fired with vars.answersVersion only after POST + status refetch both succeed.
   */
  onSubmittedAnswersVersion?: (answersVersion: number) => void
}

/**
 * Preferences submit mutation.
 * `submitPreferencesAndStatusRefresh` = POST then /status refetch; toast on failure unless silent.
 * Concurrent calls: same version joins; different version keeps only the latest pending.
 */
export function useSubmitPreferencesMutation(options?: UseSubmitPreferencesMutationOptions) {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const onSubmittedAnswersVersionRef = useRef(options?.onSubmittedAnswersVersion)
  const submitQueueRef = useRef<AnswersSubmitQueue>(createAnswersSubmitQueue())
  useEffect(() => {
    onSubmittedAnswersVersionRef.current = options?.onSubmittedAnswersVersion
  }, [options?.onSubmittedAnswersVersion])

  const mutation = useMutation({
    mutationFn: async (vars: SubmitPreferencesAnswersVars) => {
      const payload = buildSubmitPreferencesPayload(vars.questionnaireId, vars.answers)
      return submitPreferencesAnswers(payload)
    }
  })
  const { mutateAsync } = mutation

  const submitPreferencesAndStatusRefresh = useCallback(
    async (
      vars: SubmitPreferencesAnswersVars,
      { silent = false }: { silent?: boolean } = {}
    ): Promise<void> => {
      await enqueueAnswersSubmit(submitQueueRef, vars.answersVersion, async () => {
        await runExpertReviewSubmit(() => mutateAsync(vars), {
          queryClient,
          showToast,
          silent
        })
        // Only after POST + status — avoid MarkSubmitted when status refresh fails
        onSubmittedAnswersVersionRef.current?.(vars.answersVersion)
      })
    },
    [mutateAsync, queryClient, showToast]
  )

  return {
    ...mutation,
    submitPreferencesAndStatusRefresh
  }
}
